/**
 * Copyright (c) 2024 CalAmp Corp. All Rights Reserved
 */
import {useCallback, useEffect} from 'react';
import isEmpty from 'lodash.isempty';
import {PageTitle} from '@ion/applications/src/pages/admin/AdminContainer';
import UserPreferences from '@ion/core/src/preferences/UserPreferences';
import {normalize} from '@ion/core/src/utils/StringUtil';
import {PREFERENCE_NAME, SavedColumnType} from './types';
import {getSavedColumnsForPage} from './useSavedColumnView';
import {updateViewColumn} from './utils';

export interface useSavedColumnViewTypes {
  pageName?: PageTitle
};

/**
 * @useEditSavedColumnView
 *
 * Provides a Hook which returns onEditSavedColumns method which will be call when we click on Done button.
 *
 * @param pageName It is current page name.
 * @returns It returns onEditSavedColumns method which will be call when we click on Done button.
 *
 * ```typescript
 *
 *     const {onEditSavedColumns} = useEditSavedColumnView({pageName});
 *
 *     ...
 *
 *    onEditSavedColumns(selectedSavedColumnName, updatedSavedColumn)
 *
 * ```
 * @see SaveColumnEditor
 */
export const useEditSavedColumnView = ({
  pageName
}: useSavedColumnViewTypes) => {
  /**
   * Edit the selected column.
   */
  const onEditSavedColumns = useCallback(
    function(savedColumnName: string, updatedColumns?: string) {
      const name = normalize(savedColumnName || '');
      const savedColumns = UserPreferences.get(PREFERENCE_NAME) ?? [];
      const savedColumnsForPage = getSavedColumnsForPage(pageName);
      if (updatedColumns?.length > 0 && name && !isEmpty(savedColumnsForPage)) {
        const {views = []} = savedColumnsForPage;
        const updatedViews = updateViewColumn(views, name, updatedColumns);
        savedColumnsForPage.selectedViewName = name;
        savedColumnsForPage.views = [...updatedViews];
        const restSavedColumns = savedColumns?.filter(
          (page: SavedColumnType) => page.pageName !== pageName);
        UserPreferences.set(PREFERENCE_NAME, [...restSavedColumns, savedColumnsForPage]);
      }
    }, [pageName]);

  //save preferences when component / hook unmounts ...
  useEffect(() => {
    return () => {
      if (!UserPreferences.hasChangeDetection()) {
        UserPreferences.save();
      }
    };
  }, []);

  return {
    onEditSavedColumns
  };
};

export default useEditSavedColumnView;
